import React, { useMemo, useState } from 'react';
import { ButtonFilled } from 'components/ui/Button';
import { useNavigate } from 'react-router-dom';
import { useTournament } from 'context/TournamentContext';
import * as routes from 'constants/routes';
import Cookies from 'js-cookie';
import { joinTournament } from 'api/tournamentService';
import { useUser } from 'api/userService';
import { getCookieDomainFromUrl } from 'utils';
import { useWalletData } from 'context/WalletDataContext';
import { useSocket } from 'utils/hooks/useSocket';
import { formatCurrency } from 'utils';
import { CASH_TICKER } from 'utils/cash';
import { getPriceFromCache, isCacheValid } from 'utils/priceCache';
import type { RankingEntry } from 'types/tournament';
import copyIconImg from 'assets/Icon/Copyicon.svg';
import { useTournamentPrizePayout } from 'hooks/useTournamentPrizePayout';
import { mapPrizePayoutCurrencyLabel } from 'types/tournamentPrizePayout';

import WinInformationBitoMat from 'components/ui/WinInformationBitoMat';
import WinInformationCryptoSilesia from 'components/ui/WinInformationCryptoSilesia';
import WinInformationBitGet from 'components/ui/WinInformationBitGet';

import ModalSmallInfo from 'components/ui/ModalSmallInfo';

import {
  ContentInformation,
  RowInformation,
  Text,
  Position,
  SubText,
  Title,
  SubTitle,
  ContentInformationCenter,
  Wrap,
  WinWrap,
  TabsRow,
  TabsContainer,
  TabButton,
  RewardTitle,
  RewardAmount,
  RewardUsd,
  RewardDisclaimer,
  RewardSpinner,
  RewardStatus,
  RewardSubStatus,
  BreakdownSection,
  BreakdownRow,
  BreakdownLabel,
  BreakdownValue,
  InfoSection,
  InfoLabel,
  InfoValueRow,
  InfoValue,
  CopyWrap,
  CopyButton,
  CopiedText,
  ContactText,
} from './styled';

const truncateHash = (hash: string) =>
  hash.length <= 16 ? hash : hash.slice(0, 15) + '...';

const JoinEnd = (props: { tournamentDetails: any; }) => {
  const { joinedStatus, tournamentDetails, tournamentStatus, tournamentPairList } = useTournament();
  const rawData: any = tournamentDetails?.data;
  const attributes = rawData?.attributes ?? rawData?.data ?? rawData ?? {};
  const rewardPool = attributes?.reward_pool ?? 0;
  const rewardPoolCurrency = attributes?.reward_pool_currency || 'USDT';
  const displayRewardPool = rewardPool > 0 ? rewardPool + ' ' + rewardPoolCurrency: '---';
  const { totalUSDTString } = useWalletData();
  const { rank, socket, tournamentRanking } = useSocket() as { rank: number; socket: any; tournamentRanking: RankingEntry[] };
  const participantsCount = attributes?.participants?.data?.length
    ?? (Array.isArray(attributes?.final_ranking) ? attributes.final_ranking.length : undefined)
    ?? (Array.isArray(tournamentRanking) ? tournamentRanking.length : undefined);
  const endDate = attributes?.end_at ? new Date(attributes.end_at).toLocaleDateString() : '';
  const initialMoney = attributes?.amount_start || 1;
  const percentageChange = ((totalUSDTString - initialMoney) / initialMoney) * 100;
  const parseTotalMoney = formatCurrency(totalUSDTString);
  const percentageTotalss = (totalUSDTString - initialMoney);
  const { isLoggedIn, userId } = useUser();
  const navigate = useNavigate();
  const tournamentId = tournamentDetails?.data?.id;
  const tournamentIdString = tournamentId != null ? String(tournamentId) : '';
  const domain = getCookieDomainFromUrl(process.env.REACT_APP_LANDING_PAGE);
  const [isload, setLoad] = useState(false);
  const [activeTab, setActiveTab] = useState<'summary' | 'reward'>('summary');
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const tradingPair = tournamentPairList;

  // Backend stores final_ranking as a JSON string column (not a typed array),
  // so parse it before reading. Older code assumed an array and silently
  // failed when the field came back as a string — winnerUserId stayed null
  // and isWinner read as false even for the actual winner.
  const finalRanking = (() => {
    const raw = attributes?.final_ranking;
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string' && raw.length > 0) {
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    }
    return null;
  })();
  const winnerUserId = Array.isArray(finalRanking)
    ? finalRanking.find((r: any) => r?.rank === 1)?.user?.id
    : null;
  const isWinner =
    joinedStatus &&
    winnerUserId != null &&
    userId != null &&
    String(winnerUserId) === String(userId);

  const winnerPayout = attributes?.winner_payout;
  const winnerPayoutNumber = Number(winnerPayout ?? 0);
  const rewardPolAmount = isWinner && Number.isFinite(winnerPayoutNumber) ? winnerPayoutNumber : 0;
  const isManualPayout = !!attributes?.manual_payout;

  // Chain-aware ticker for reward display + USD conversion. Backend stake_currency
  // is the authoritative source post-Solana epic; legacy reward_pool_currency string
  // is the fallback for tournaments created before the relation existed.
  const stakeCurrencyTicker: string =
    attributes?.stake_currency?.short_name ?? attributes?.reward_pool_currency ?? 'POL';

  const cachedUsdApprox = useMemo(() => {
    if (rewardPolAmount <= 0) {
      return null;
    }

    const cached = getPriceFromCache(stakeCurrencyTicker);
    if (!isCacheValid(cached)) {
      return null;
    }

    return rewardPolAmount * cached!.price;
  }, [rewardPolAmount, stakeCurrencyTicker]);

  const displayUsdApprox = useMemo(() => {
    if (rewardPolAmount === 0) {
      return 0;
    }

    return cachedUsdApprox;
  }, [rewardPolAmount, cachedUsdApprox]);

  const isPayoutEligible = isWinner && rewardPolAmount > 0;

  const {
    payout,
    viewState: payoutViewState,
    isLoading: isPayoutLoading,
    error: payoutError,
  } = useTournamentPrizePayout({
    // TEAM_005: Winner-only payout status (socket primary + REST fallback).
    // Manual-payout: any joined user can have OVERPAYMENT_REFUND.
    tournamentId: tournamentIdString,
    isWinner: isPayoutEligible,
    socket,
    enabled: tournamentStatus === 'FINISHED',
    ...(isManualPayout && {
      expectsReward: false,
      lookupEnabled: !!joinedStatus,
    }),
  });

  // Old tournaments (pre-Solana epic) have no stake_currency relation — there is no
  // on-chain payout to surface, so the Reward tab would only show empty placeholders.
  const hasStakeCurrency = attributes?.stake_currency != null;
  const showPayoutTab = hasStakeCurrency && payoutViewState !== 'NO_PAYOUT';
  const isRefundView = payout?.payout_type === 'OVERPAYMENT_REFUND';
  const effectiveTab = showPayoutTab ? activeTab : 'summary';

  var defeatInfo;
  var winPersonBitGet;
  var winPersonBitoMat;
  var winPersonCryptoSilesia;
  var modalSmallInfo;

  if (rank === 0 || rank > 3) {
    defeatInfo = <SubTitle className="SecondSubTitle">Thank you!</SubTitle>;
  }

  if (tournamentId === 85) {
    if (rank === 0 || rank > 33) {
      defeatInfo = <SubTitle className="SecondSubTitle">Thank you!</SubTitle>;
    } else {
      winPersonBitoMat = <WinInformationBitoMat/>;
    }
  }

  if (tournamentId === 87) {
    winPersonBitGet = <WinInformationBitGet/>;
  }

  if (tournamentId === 120) {
    if (rank === 0 || rank > 3) {
      defeatInfo = <SubTitle className="SecondSubTitle">Thank you!</SubTitle>;
    } else {
      winPersonBitoMat = <WinInformationCryptoSilesia/>;
    }
  }

  if (tournamentId === 126) {
    modalSmallInfo = <ModalSmallInfo />;
  }

  const handleJoinClick = async () => {
    try {
      setLoad(!isload);
      if (!isLoggedIn) {
        localStorage.setItem('intendToJoinTournament', tournamentIdString);
        Cookies.set('intendToJoinTournament', tournamentIdString, { expires: 7, secure: true, sameSite: 'none', domain });
        navigate(routes.HOME);
        return;
      }

      // PAYMENT CHECK - Hard block paid tournaments (Task 14.4)
      // This "join another tournament" convenience button doesn't have payment UI.
      // Paid tournaments must be joined through the main tournaments page.
      const entryFee = attributes?.entryFee;
      if (entryFee?.type === 'PAID') {
        alert(
          `This tournament requires payment (${entryFee.amount} ${entryFee.currency}). ` +
          `Please join from the tournaments list page to complete payment.`
        );
        setLoad(false);
        return;
      }

      if (!joinedStatus) {
        // Free tournament only - no paymentId
        await joinTournament(tournamentId);
      }

      const newPath = `${routes.GAME.replace(':id', tournamentIdString)}`;
      window.location.href = newPath;

    } catch (error: any) {
      setLoad(false);
      
      // Handle payment-specific errors (Task 14.3)
      if (error?.uiState) {
        alert(`Payment required: ${error.uiState}. Please join from the tournaments list page.`);
        return;
      }
      
      // Handle standard API errors
      if (error?.error?.status === 409 && error?.error?.name === 'ConflictError') {
        window.location.href = `${routes.GAME.replace(':id', tournamentIdString)}`;
      } else {
        console.error('Error joining tournament:', error);
        alert('An error occurred while joining the tournament. Please try again.');
      }
    }
  };

  const renderPayoutStatusIndicator = (label: 'Payout' | 'Refund') => {
    const item = label === 'Refund' ? 'refund' : 'reward';

    if (payoutError) {
      return <RewardStatus>{label} status unavailable</RewardStatus>;
    }

    if (isPayoutLoading && !payout) {
      return <RewardStatus>Loading {label.toLowerCase()} status...</RewardStatus>;
    }

    if (payoutViewState === 'QUEUED' || payoutViewState === 'PENDING' || payoutViewState === 'INITIATED') {
      return (
        <>
          <RewardSpinner />
          <RewardSubStatus>Your {item} will be sent{'\n'}within 24 hours...</RewardSubStatus>
        </>
      );
    }

    if (payoutViewState === 'CONFIRMED') {
      return <RewardStatus>{label} confirmed</RewardStatus>;
    }

    if (payoutViewState === 'FAILED') {
      return (
        <>
          <RewardStatus>{label} failed</RewardStatus>
          {(payout?.error_message || payout?.error_code) && (
            <RewardSubStatus>
              {payout.error_message || payout.error_code}
            </RewardSubStatus>
          )}
        </>
      );
    }

    return null;
  };

  const renderRewardStatus = () => {
    if (!isPayoutEligible) {
      if (isWinner && rewardPolAmount <= 0) {
        return <RewardStatus>No payout</RewardStatus>;
      }
      return null;
    }
    return renderPayoutStatusIndicator('Payout');
  };

  const renderRefundStatus = () => renderPayoutStatusIndicator('Refund');

  const formatPol = (value: number): string => {
    if (value === 0) return '0.00';
    // 9 decimals covers SOL lamport precision (1e-9) and typical POL display range;
    // trailing zeros trimmed, minimum 2 decimals for visual consistency
    const str = value.toFixed(9);
    const [intPart, decPart] = str.split('.');
    const trimmed = decPart.replace(/0+$/, '');
    const final = trimmed.length < 2 ? trimmed.padEnd(2, '0') : trimmed;
    return intPart + '.' + final;
  };

  const handleCopy = async (value: string, field: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(prev => prev === field ? null : prev), 2000);
    } catch {
      // Ignore clipboard errors
    }
  };

  return (
    <Wrap className="wrapModalEnd">
      {modalSmallInfo}
      {effectiveTab === 'summary' ? (
        <>
          <SubTitle>{attributes?.users_limit === 2 ? 'Battle summary' : 'Tournament summary'}</SubTitle>
          <Title>
            {attributes?.users_limit === 2
              ? (rank === 1
                  ? <>You are a <span className="up_state">winner!</span></>
                  : <>You have been <span className="down_state">defeated</span></>)
              : attributes?.name
            }
          </Title>
        </>
      ) : isRefundView ? (
        <>
          <SubTitle>Overpayment refund</SubTitle>
          <Title>Refund details</Title>
        </>
      ) : (
        <>
          <SubTitle>{isWinner ? 'Congratulations!' : 'Other traders were more successful'}</SubTitle>
          <Title>
            {isWinner
              ? <>You're among the <span className="up_state">winners!</span></>
              : <>You have been <span className="down_state">defeated</span></>}
          </Title>
        </>
      )}

      <TabsRow>
        <TabsContainer>
          <TabButton
            type="button"
            $selected={effectiveTab === 'summary'}
            onClick={() => setActiveTab('summary')}
          >
            Summary
          </TabButton>
          {showPayoutTab && (
            <TabButton
              type="button"
              $selected={effectiveTab === 'reward'}
              onClick={() => setActiveTab('reward')}
            >
              {isRefundView ? 'Refund' : 'Reward'}
            </TabButton>
          )}
        </TabsContainer>
      </TabsRow>

      {effectiveTab === 'summary' ? (
        <>
          <ContentInformation>
            <RowInformation>
              <Text>Market:</Text>
              <Text>Cryptocurrency</Text>
            </RowInformation>
            <RowInformation>
              <Text>Type:</Text>
              <Text>{attributes?.trade_type}</Text>
            </RowInformation>
            <RowInformation>
              <Text>Trading pair:</Text>
              <Text>{tradingPair.length === 0 ? `` : tradingPair?.length > 1 ? `Multi assets` : tradingPair[0]?.attributes?.name}</Text>
            </RowInformation>
            {attributes?.users_limit !== 2 && (
              <RowInformation>
                <Text>Tournament time:</Text>
                <Text>
                  {(tournamentStatus === 'FINISHED') ? (
                    <>Finished</>
                  ) : <>Ends in {endDate}</>}
                </Text>
              </RowInformation>
            )}
            {attributes?.users_limit === 2 ? (
              <RowInformation>
                <Text>Results %:</Text>
                <Text>
                  <span className={`${percentageChange >= 0 ? 'up_state' : 'down_state'}`}>
                    {percentageChange.toFixed(2)}%
                  </span>
                </Text>
              </RowInformation>
            ) : (
              <>
                <RowInformation>
                  <Text>Result:</Text>
                  <Text>
                    {joinedStatus ? (
                      <span className={`${percentageChange >= 0 ? 'up_state' : 'down_state'}`}>
                        {parseTotalMoney} {CASH_TICKER}
                      </span>
                    ) : <>-</>}
                  </Text>
                </RowInformation>
                <RowInformation>
                  <Text>Profit/Loss:</Text>
                  <Text>
                    {joinedStatus ? (
                      <span className={`${percentageChange >= 0 ? 'up_state' : 'down_state'}`}>
                        {percentageTotalss.toFixed(2)} {CASH_TICKER} ({percentageChange >= 0 ? '+' : percentageChange <= 0 ? '' : ''}{percentageChange.toFixed(4)}%)
                      </span>
                    ) : <>-</>}
                  </Text>
                </RowInformation>
              </>
            )}
            {joinedStatus && rewardPool > 0 && !isManualPayout ? (
              <RowInformation>
                <Text>Reward:</Text>
                <Text>
                  {displayRewardPool}
                </Text>
              </RowInformation>
            ) : null }
          </ContentInformation>
          {!joinedStatus && (tournamentStatus === 'FINISHED') ? (
            <>
              {!isLoggedIn ? (
                <ButtonFilled className="btn_large" type="submit" onClick={handleJoinClick}>Log in</ButtonFilled>
              ) :
                <ButtonFilled className="btn_large" type="submit" disabled>Finished</ButtonFilled>
              }
            </>
          ) :
            <>
              {rewardPool > 0 && (
                <WinWrap>
                  {winPersonBitGet}
                  {winPersonBitoMat}
                  {winPersonCryptoSilesia}
                  {defeatInfo}
                </WinWrap>
              )}
              <ContentInformationCenter>
                <Text>Your position:</Text>
                <Position $isFirst={rank === 1}>{rank}</Position>
                <SubText>(out of {participantsCount} participants)</SubText>
              </ContentInformationCenter>
              {/*
                <ButtonFilled
                onClick={() => navigate(routes.CREATING_TOURNAMENT)}
                style={{ marginTop: '2rem' }}
              >
                Create New
              </ButtonFilled>
              */}
            </>
          }
        </>
      ) : isRefundView && payout ? (
        <>
          <RewardTitle>Overpayment Refund</RewardTitle>
          <RewardDisclaimer>Your overpayment is being refunded automatically</RewardDisclaimer>
          <RewardAmount>
            {formatPol(Number(payout.overpayment ?? 0))} {mapPrizePayoutCurrencyLabel(payout.currency)}
          </RewardAmount>
          {renderRefundStatus()}

          {payout.tx_hash && (
            <InfoSection>
              <InfoLabel>Transaction ID:</InfoLabel>
              <InfoValueRow>
                <InfoValue>{truncateHash(payout.tx_hash)}</InfoValue>
                <CopyWrap>
                  {copiedField === 'txHash' && <CopiedText>Copied!</CopiedText>}
                  <CopyButton
                    type="button"
                    aria-label="Copy transaction id"
                    onClick={() => handleCopy(payout.tx_hash!, 'txHash')}
                  >
                    <img src={copyIconImg} alt="Copy" />
                  </CopyButton>
                </CopyWrap>
              </InfoValueRow>
            </InfoSection>
          )}

          <InfoSection>
            <InfoLabel>Game number:</InfoLabel>
            <InfoValueRow>
              <InfoValue>{tournamentIdString}</InfoValue>
              <CopyWrap>
                {copiedField === 'gameNumber' && <CopiedText>Copied!</CopiedText>}
                <CopyButton
                  type="button"
                  aria-label="Copy game number"
                  onClick={() => handleCopy(tournamentIdString, 'gameNumber')}
                >
                  <img src={copyIconImg} alt="Copy" />
                </CopyButton>
              </CopyWrap>
            </InfoValueRow>
          </InfoSection>

          <ContactText>
            If you encounter any issues with your refund, please contact us at{' '}
            <strong>contact@tradingbattles.com</strong>{' '}
            and provide your transaction ID and game number
          </ContactText>
        </>
      ) : (
        <>
          <RewardTitle>Reward*</RewardTitle>
          <RewardDisclaimer>*Reward does not include network payout fees</RewardDisclaimer>
          <RewardAmount>
            {formatPol(rewardPolAmount)} {mapPrizePayoutCurrencyLabel(payout?.currency ?? stakeCurrencyTicker)}
          </RewardAmount>
          {displayUsdApprox != null && (
            <RewardUsd>≈{parseFloat(Number(displayUsdApprox).toFixed(4))} USD</RewardUsd>
          )}
          {renderRewardStatus()}

          {isPayoutEligible && payout && (() => {
            const currencyLabel = mapPrizePayoutCurrencyLabel(payout.currency ?? stakeCurrencyTicker);
            const finalPool = Number(attributes?.final_pool ?? 0);
            const platformFee = Number(attributes?.platform_fee_collected ?? 0);
            const platformFeePercent = finalPool > 0 ? Math.round((platformFee / finalPool) * 100) : 0;
            const gasFee = payout.gas_fee_deducted != null ? Number(payout.gas_fee_deducted) : null;
            const netReward = gasFee != null ? finalPool - platformFee - gasFee : finalPool - platformFee;

            return (
              <>
                <BreakdownSection>
                  <BreakdownRow>
                    <BreakdownLabel>Reward pool:</BreakdownLabel>
                    <BreakdownValue>{formatPol(finalPool)} {currencyLabel}</BreakdownValue>
                  </BreakdownRow>
                  {platformFee > 0 && (
                    <BreakdownRow>
                      <BreakdownLabel>Platform fee:</BreakdownLabel>
                      <BreakdownValue>-{formatPol(platformFee)} {currencyLabel} ({platformFeePercent}%)</BreakdownValue>
                    </BreakdownRow>
                  )}
                  <BreakdownRow>
                    <BreakdownLabel>Transaction fee:</BreakdownLabel>
                    <BreakdownValue>
                      {gasFee != null
                        ? `-${formatPol(gasFee)} ${currencyLabel}`
                        : 'Pending...'}
                    </BreakdownValue>
                  </BreakdownRow>
                  <BreakdownRow>
                    <BreakdownLabel>Your reward:</BreakdownLabel>
                    <BreakdownValue>{formatPol(netReward)} {currencyLabel}</BreakdownValue>
                  </BreakdownRow>
                  {Number(payout.overpayment ?? 0) > 0 && (
                    <BreakdownRow>
                      <BreakdownLabel>Overpayment refund:</BreakdownLabel>
                      <BreakdownValue>+{formatPol(Number(payout.overpayment ?? 0))} {currencyLabel}</BreakdownValue>
                    </BreakdownRow>
                  )}
                </BreakdownSection>

                {payout.tx_hash && (
                  <InfoSection>
                    <InfoLabel>Transaction ID:</InfoLabel>
                    <InfoValueRow>
                      <InfoValue>{truncateHash(payout.tx_hash)}</InfoValue>
                      <CopyWrap>
                        {copiedField === 'txHash' && <CopiedText>Copied!</CopiedText>}
                        <CopyButton
                          type="button"
                          aria-label="Copy transaction id"
                          onClick={() => handleCopy(payout.tx_hash!, 'txHash')}
                        >
                          <img src={copyIconImg} alt="Copy" />
                        </CopyButton>
                      </CopyWrap>
                    </InfoValueRow>
                  </InfoSection>
                )}

                <InfoSection>
                  <InfoLabel>Game number:</InfoLabel>
                  <InfoValueRow>
                    <InfoValue>{tournamentIdString}</InfoValue>
                    <CopyWrap>
                      {copiedField === 'gameNumber' && <CopiedText>Copied!</CopiedText>}
                      <CopyButton
                        type="button"
                        aria-label="Copy game number"
                        onClick={() => handleCopy(tournamentIdString, 'gameNumber')}
                      >
                        <img src={copyIconImg} alt="Copy" />
                      </CopyButton>
                    </CopyWrap>
                  </InfoValueRow>
                </InfoSection>

                <ContactText>
                  If you encounter any issues with receiving your prize, please contact us at{' '}
                  <strong>contact@tradingbattles.com</strong>{' '}
                  and provide your transaction ID and game number
                </ContactText>
              </>
            );
          })()}
        </>
      )}
    </Wrap>
  );
};
export default JoinEnd;
